import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import {
  getPublicSubscriptionPlanCatalog,
  getSchoolSubscriptionSummary,
  type PublicPlanCatalogRow,
  type SchoolSubscriptionSummaryRow,
} from '../modules/billing/api/billing.client';
import { isBillingActivationPending, clearBillingActivationPending } from '../modules/billing/checkoutActivation';
import { normalizeSubscriptionStatus } from '../modules/billing/subscriptionDisplay';
import { USER_ROLES } from '../utils/constants';
import type { Membership } from '../modules/platform/types/user';

export const SCHOOL_SUBSCRIPTION_QUERY_KEY = 'school-subscription-summary';
export const SCHOOL_PLAN_CATALOG_QUERY_KEY = 'school-plan-catalog-public';

type SchoolSubscriptionContextValue = {
  schoolId: string;
  summary: SchoolSubscriptionSummaryRow | null;
  catalog: PublicPlanCatalogRow[];
  assignedPlanCatalog: PublicPlanCatalogRow | undefined;
  loading: boolean;
  refresh: () => Promise<void>;
};

const SchoolSubscriptionContext = createContext<SchoolSubscriptionContextValue | null>(null);

function resolveSchoolId(user: ReturnType<typeof useAuth>['user']): string {
  return (
    (typeof localStorage !== 'undefined' ? localStorage.getItem('X_TENANT_ID') : null)?.trim() ||
    user?.memberships?.find((m: Membership) => m.school_id)?.school_id ||
    ''
  );
}

export const SchoolSubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isSchoolAdmin = Boolean(user?.roles?.includes(USER_ROLES.SCHOOL_ADMIN));
  const schoolId = useMemo(() => resolveSchoolId(user), [user]);

  const summaryQuery = useQuery({
    queryKey: [SCHOOL_SUBSCRIPTION_QUERY_KEY, schoolId],
    queryFn: () => getSchoolSubscriptionSummary(schoolId),
    enabled: isSchoolAdmin && Boolean(schoolId),
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      const status = (query.state.data?.status || '').toLowerCase();
      if (schoolId && isBillingActivationPending(schoolId)) {
        return 5_000;
      }
      return status === 'trial' || status === 'past_due' || status === 'expired' ? 60_000 : false;
    },
    retry: 1,
  });

  React.useEffect(() => {
    if (summaryQuery.data && normalizeSubscriptionStatus(summaryQuery.data.status) === 'active') {
      clearBillingActivationPending(schoolId);
    }
  }, [summaryQuery.data?.status, schoolId]);

  const catalogQuery = useQuery({
    queryKey: [SCHOOL_PLAN_CATALOG_QUERY_KEY],
    queryFn: getPublicSubscriptionPlanCatalog,
    enabled: isSchoolAdmin,
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const assignedPlanCatalog = useMemo(() => {
    const pk = (summaryQuery.data?.plan_key || '').toUpperCase();
    if (!pk) return undefined;
    return catalogQuery.data?.find((p) => p.plan_key.toUpperCase() === pk);
  }, [summaryQuery.data?.plan_key, catalogQuery.data]);

  const refresh = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [SCHOOL_SUBSCRIPTION_QUERY_KEY, schoolId] }),
      queryClient.invalidateQueries({ queryKey: [SCHOOL_PLAN_CATALOG_QUERY_KEY] }),
    ]);
  }, [queryClient, schoolId]);

  const value = useMemo<SchoolSubscriptionContextValue>(
    () => ({
      schoolId,
      summary: summaryQuery.data ?? null,
      catalog: catalogQuery.data ?? [],
      assignedPlanCatalog,
      loading: summaryQuery.isLoading || (isSchoolAdmin && !schoolId ? false : summaryQuery.isFetching && !summaryQuery.data),
      refresh: async () => {
        await refresh();
      },
    }),
    [
      schoolId,
      summaryQuery.data,
      summaryQuery.isLoading,
      summaryQuery.isFetching,
      catalogQuery.data,
      assignedPlanCatalog,
      isSchoolAdmin,
      refresh,
    ]
  );

  if (!isSchoolAdmin) {
    return <>{children}</>;
  }

  return <SchoolSubscriptionContext.Provider value={value}>{children}</SchoolSubscriptionContext.Provider>;
};

export function useSchoolSubscription(): SchoolSubscriptionContextValue {
  const ctx = useContext(SchoolSubscriptionContext);
  if (!ctx) {
    return {
      schoolId: '',
      summary: null,
      catalog: [],
      assignedPlanCatalog: undefined,
      loading: false,
      refresh: async () => {},
    };
  }
  return ctx;
}

export function invalidateSchoolSubscription(queryClient: ReturnType<typeof useQueryClient>, schoolId?: string) {
  if (schoolId) {
    void queryClient.invalidateQueries({ queryKey: [SCHOOL_SUBSCRIPTION_QUERY_KEY, schoolId] });
  } else {
    void queryClient.invalidateQueries({ queryKey: [SCHOOL_SUBSCRIPTION_QUERY_KEY] });
  }
}
